import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, FormArray, FormControl, Validators, FormBuilder } from '@angular/forms';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { PreMoldInfo } from '../../models/PreMoldInfo';
import { PaginatedPreMoldInfo, PaginatedPreMoldTreadResponse, Pagination, PreMoldTread } from '../../models/response.model';
import { PreMoldFilters } from '../../models/PreMoldFilters';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { DealerState } from '../../../../shared/state/DealerState';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { getDateInMMddyyyyFormat } from '../../../../shared/helper/date-time-helper';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { environment } from '../../../../../environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-pre-mold-screen',
  templateUrl: './pre-mold-screen.component.html',
  styleUrls: ['./pre-mold-screen.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreMoldScreenComponent implements OnInit {
  @Input() preMoldInfo: PreMoldInfo;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;

  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public store = inject(Store);
  public fb = inject(FormBuilder);
  public formBuilder = inject(UntypedFormBuilder);
  public route = inject(ActivatedRoute);
  public cdr = inject(ChangeDetectorRef);
  public columnPrefService = inject(ColumnPreferencesService);
  public isProduction = environment.production;
  public columnPrefsHelper = inject(ColumnPreferencesHelperService);

  treadsList: PreMoldTread[] = [];
  rollPartNumbers: string[] = [];
  hasFetchedRollParts = false;
  pagination: Pagination;
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  tabRouteLink: string = '/productData';
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  selectedImage: string | null = null;
  selectedImageLabel: string | null = null;
  isImageDialogOpen = false;
  showPopup = true;
  treads: string[];
  applications: string[];
  treadWidths: string[];
  axles: string[];
  treadBrands: string[];
  casingSizes: string[];
  imageData = '';
  productSaveDisabled = false;
  saveEnabled = false;
  isModified = false;
  filterDropdownWidth = '200px';
  isExporting = false;
  columnNames: Record<string, string> = {};
  private dialog = inject(MatDialog);

  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  private ngUnsubscribe = new Subject<void>();

  tableConfig: TableConfig<PreMoldTread> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };
  state = TableConfig.getState(this.tableConfig, this.route);

  formGroup = this.formBuilder.group({
    treads: [this.state.extraFilters?.tread ?? []],
    treadBrands: [this.state.extraFilters?.treadBrand ?? []],
    treadWidths: [this.state.extraFilters?.treadWidth ?? []],
    casingSizes: [this.state.extraFilters?.casingSize ?? []],
    axles: [this.state.extraFilters?.axle ?? []],
    applications: [this.state.extraFilters?.application ?? []]
  });
  fetcher: PaginatorFetcher<PreMoldTread> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    const preMoldFilters: PreMoldFilters = this.generateFilters();
    return this.productDataService.getPreMoldLines(preMoldFilters, filters.page, filters.limit).pipe(
      map((preMoldResponse: PaginatedPreMoldTreadResponse) => {
        if (preMoldResponse && preMoldResponse.error && preMoldResponse.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: 'Please try again. Something went wrong.'
          } as Alert);
          return { items: [], count: 0 };
        } else {
          this.treadsList = preMoldResponse.data.treads;
          this.pagination = preMoldResponse.data.pagination;

          // Extract any dealerCTC and dealerTreadNo values from the fetched rows
          const tableDealerCTC = this.treadsList.map((tread) => tread.dealerCTC).filter((val): val is string => !!val);
          const tableDealerTreadNos = this.treadsList.map((tread) => tread.dealerTreadNo).filter((val): val is string => !!val);

          // TEMPORARY fallback list for dropdowns
          const tempRollPartNumbers = Array.from(new Set([...tableDealerCTC, ...tableDealerTreadNos]));

          // If rollPartNumbers hasn't loaded yet, use the fallback
          if (!this.hasFetchedRollParts) {
            this.rollPartNumbers = tempRollPartNumbers.sort();
          }

          this.updateDisplayedRowsForm(this.treadsList);
          return {
            items: this.treadsList,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  exportDataFetcher: (selectedColumns?: string[]) => Observable<ExportResponse> = () => {
    const MAX_ROWS = 10000;
    const PAGE_SIZE = 5000;
    const filters = this.generateFilters();

    return new Observable<ExportResponse>((subscriber) => {
      let page = 1;
      const allRows: PreMoldTread[] = [];

      const fetchPage = () => {
        // Explicitly ask backend to exclude images to avoid large payloads.
        // No fields projection is required because the controller's `excludeImages` flag
        // will null out image data server-side while returning the other fields needed
        // for export (zoneRollNo will still be included in the response).
        this.productDataService.getPreMoldLines(filters, page, PAGE_SIZE, undefined, true).subscribe({
          next: (response: any) => {
            const items: PreMoldTread[] = (response?.data?.treads as PreMoldTread[]) || [];
            allRows.push(...items);

            const totalRecords: number = response?.data?.pagination?.totalRecords ?? allRows.length;

            // If we received a full page and haven't reached limits, fetch next
            if (items.length === PAGE_SIZE && allRows.length < Math.min(totalRecords, MAX_ROWS)) {
              page++;
              fetchPage();
            } else {
              // Trim to MAX_ROWS
              const capped = allRows.slice(0, MAX_ROWS);
              const mapped = capped.map((item) => ({
                zoneRollNo: item.zoneRollNo,
                treadMake: item.treadMake,
                treadBrand: item.treadBrand,
                tread: item.tread,
                casingSize: item.casingSize,
                treadWidth: item.treadWidth,
                dealerTreadNo: item.dealerTreadNo,
                dealerCTC: item.dealerCTC,
                zoneCTC: item.zoneCTC,
                sku: item.sku,
                application: item.application,
                axle: item.axle,
                treadLength: item.treadLength,
                treadDepth: item.treadDepth,
                treadWeight: item.treadWeight,
                cushionUnitWt: item.cushionUnitWt,
                sideWallNo: item.sideWallNo,
                sideWallWidth: item.sideWallWidth,
                m3: item.m3,
                treadSI: item.treadSI,
                ms: item.ms,
                pmsf: item.pmsf,
                updatedOn: item.lastUpdtTimestamp
              }));

              subscriber.next({ data: mapped });
              subscriber.complete();
            }
          },
          error: (err) => {
            subscriber.error(err);
          }
        });
      };

      fetchPage();
    });
  };

  constructor() {}
  displayedRowsForm = this.formBuilder.group({
    rows: this.formBuilder.array([])
  });

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.treads = this.store.selectSnapshot(ProductsState.products).allTreads;
    this.treadBrands = this.store.selectSnapshot(ProductsState.products).treadBrands;
    this.treadWidths = this.store.selectSnapshot(ProductsState.products).widths;
    this.applications = this.store.selectSnapshot(ProductsState.products).applications;
    this.axles = this.store.selectSnapshot(ProductsState.products).axles;
    this.casingSizes = this.store.selectSnapshot(ProductsState.products).sizes;
    this.setupFormListeners();
    this.loadInitialData();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        // build canonical columns (used as source of truth)
        this.canonicalColumns = {
          zoneRollNo: { cell: (elm: PreMoldTread) => elm.zoneRollNo, header: this.translationService.translate('zoneRollText') },
          treadMake: { cell: (elm: PreMoldTread) => elm.treadMake, header: this.translationService.translate('treadMakeText') },
          treadBrand: { cell: (elm: PreMoldTread) => elm.treadBrand, header: this.translationService.translate('treadBrandText') },
          tread: { cell: (elm: PreMoldTread) => elm.tread, header: this.translationService.translate('treadText') },
          casingSize: { cell: (elm: PreMoldTread) => elm.casingSize, header: this.translationService.translate('casingSizesText') },
          treadWidth: { cell: (elm: PreMoldTread) => elm.treadWidth, header: this.translationService.translate('treadWidthColumnText') },
          dealerTreadNo: { header: this.translationService.translate('dealerTreadRollText'), cell: 'dealerTreadDropdown' },
          dealerCTC: { cell: 'dealerCTCDropdown', header: this.translationService.translate('dealerCTCText') },
          zoneCTC: { cell: (elm: PreMoldTread) => elm.zoneCTC, header: this.translationService.translate('zoneCTCText') },
          sku: { cell: (elm: PreMoldTread) => elm.sku, header: this.translationService.translate('skuText') },
          application: { cell: (elm: PreMoldTread) => elm.application, header: this.translationService.translate('applicationText') },
          axle: { cell: (elm: PreMoldTread) => elm.axle, header: this.translationService.translate('axleText') },
          treadLength: { cell: (elm: PreMoldTread) => elm.treadLength, header: this.translationService.translate('treadLengthText') },
          treadDepth: { cell: (elm: PreMoldTread) => elm.treadDepth, header: this.translationService.translate('treadDepthText') },
          treadWeight: { cell: (elm: PreMoldTread) => elm.treadWeight, header: this.translationService.translate('treadWeightText') },
          cushionUnitWt: { cell: (elm: PreMoldTread) => elm.cushionUnitWt, header: this.translationService.translate('cushionUnitWtText') },
          sideWallNo: { cell: 'sideWallNoInput', header: this.translationService.translate('sideWallNoText') },
          sideWallWidth: { cell: 'sideWallWidthInput', header: this.translationService.translate('sideWallWidthText') },
          m3: { cell: (elm: PreMoldTread) => elm.m3, header: this.translationService.translate('m3Text') },
          treadSI: { cell: (elm: PreMoldTread) => elm.treadSI, header: this.translationService.translate('treadSIText') },
          ms: { cell: (elm: PreMoldTread) => elm.ms, header: this.translationService.translate('msText') },
          pmsf: { cell: (elm: PreMoldTread) => elm.pmsf, header: this.translationService.translate('pmsfText') },
          updatedOn: {
            cell: (elm: PreMoldTread) => getDateInMMddyyyyFormat(elm.lastUpdtTimestamp),
            header: this.translationService.translate('updatedOnColumnText')
          },
          treadImage: { cell: 'treadImageCell', header: this.translationService.translate('treadImageText') }
        };

        this.columnNames = {
          zoneRollNo: this.translationService.translate('zoneRollText'),
          treadMake: this.translationService.translate('treadMakeText'),
          treadBrand: this.translationService.translate('treadBrandText'),
          tread: this.translationService.translate('treadText'),
          casingSize: this.translationService.translate('casingSizesText'),
          treadWidth: this.translationService.translate('treadWidthColumnText'),
          dealerTreadNo: this.translationService.translate('dealerTreadRollText'),
          dealerCTC: this.translationService.translate('dealerCTCText'),
          zoneCTC: this.translationService.translate('zoneCTCText'),
          sku: this.translationService.translate('skuText'),
          application: this.translationService.translate('applicationText'),
          axle: this.translationService.translate('axleText'),
          treadLength: this.translationService.translate('treadLengthText'),
          treadDepth: this.translationService.translate('treadDepthText'),
          treadWeight: this.translationService.translate('treadWeightText'),
          cushionUnitWt: this.translationService.translate('cushionUnitWtText'),
          sideWallNo: this.translationService.translate('sideWallNoText'),
          sideWallWidth: this.translationService.translate('sideWallWidthText'),
          m3: this.translationService.translate('m3Text'),
          treadSI: this.translationService.translate('treadSIText'),
          ms: this.translationService.translate('msText'),
          pmsf: this.translationService.translate('pmsfText'),
          updatedOn: this.translationService.translate('updatedOnColumnText'),
          treadImage: this.translationService.translate('treadImageText')
        };

        // apply user preferences (localStorage) and initialize tableConfig.columns
        const init = this.columnPrefsHelper.initialize('pre-mold', this.canonicalColumns, {
          minVersion: 1,
          defaultVisibility: {
            application: false,
            axle: false,
            treadLength: false,
            treadDepth: false,
            treadWeight: false,
            cushionUnitWt: false,
            sideWallNo: false,
            sideWallWidth: false,
            m3: false,
            treadSI: false,
            ms: false,
            pmsf: false
          }
        });
        this.tableConfig.columns = init.tableColumns;
        this.columnNames = init.columnNames;
        this.columnKeys = init.columnKeys;
        this.columnOrder = init.columnOrder;
        this.columnVisibility = init.columnVisibility;
        this.computeFilterDropdownWidth();
        this.cdr.markForCheck();
      });
    this.tableRef.patchExtraFilters(this.formGroup.value, false);
    this.computeFilterDropdownWidth();
  }

  private computeFilterDropdownWidth(): void {
    const t = (key: string) => this.translationService.translate(key);
    const placeholders = [
      t('treadSearchText'),
      t('selectTreadWidthText'),
      t('selectTreadBrandText'),
      t('selectApplicationText'),
      t('selectAxleText'),
      t('selectCasingSizeText')
    ];
    const maxLen = placeholders.reduce((max, p) => Math.max(max, p.length), 0);
    this.filterDropdownWidth = `${Math.max(200, maxLen * 8 + 56)}px`;
    this.cdr.markForCheck();
  }

  get dropdownWidthConfig(): { width: string } {
    const maxLen = this.rollPartNumbers.reduce((max, val) => Math.max(max, (val ?? '').length), 0);
    return { width: `${Math.max(150, maxLen * 9 + 48)}px` };
  }

  private setupFormListeners(): void {
    this.formGroup.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((values) => {
      this.tableRef.patchExtraFilters(values, true);
    });
  }
  private loadInitialData(): void {
    const rollPartNumbers = this.store.selectSnapshot(ProductsState.products).rollPartNumbers ?? [];

    // Avoid replacing values if it's already been set from fetcher fallback
    this.rollPartNumbers = Array.from(new Set([...this.rollPartNumbers, ...rollPartNumbers])).sort();
    this.hasFetchedRollParts = true;
  }
  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave('pre-mold', key, this.canonicalColumns, this.columnOrder, this.columnVisibility);
    this.tableConfig = {
      ...this.tableConfig,
      columns: result.tableColumns
    };
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  onColumnDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.columnOrder, event.previousIndex, event.currentIndex);

    const columns: Record<string, any> = {};
    this.columnOrder.forEach((key) => {
      if (this.columnVisibility[key]) {
        columns[key] = this.canonicalColumns[key];
      }
    });

    this.tableConfig = {
      ...this.tableConfig,
      columns
    };
    this.columnPrefsHelper.save('pre-mold', this.columnOrder, this.columnVisibility, this.canonicalColumns);
    this.cdr.markForCheck();
  }

  resetFilters(): void {
    this.formGroup.reset();
  }
  exportTable(): void {
    this.openExportModal();
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-PreMold-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }
  private createRowForm(row: PreMoldTread) {
    return this.formBuilder.group({
      zoneRollNo: [row.zoneRollNo],
      treadMake: [row.treadMake],
      treadBrand: [row.treadBrand],
      tread: [row.tread],
      casingSize: [row.casingSize],
      treadWidth: [row.treadWidth],
      dealerTreadNo: [row.dealerTreadNo],
      dealerCTC: [row.dealerCTC],
      zoneCTC: [row.zoneCTC],
      sku: [row.sku],
      application: [row.application],
      applicationFk: [row.applicationFk],
      axle: [row.axle],
      axleFk: [row.axleFk],
      m3: [row.m3],
      ms: [row.ms],
      pmsf: [row.pmsf],
      treadLength: [row.treadLength],
      treadDepth: [row.treadDepth],
      treadWeight: [row.treadWeight],
      treadSI: [row.treadSI],
      treadDimensionPk: [row.treadDimensionPk],
      treadImage: [row.treadImage],
      treadImageName: [row.treadImageName],
      cushionUnitWt: [row.cushionUnitWt],
      sideWallWidth: [row.sideWallWidth, [Validators.required, Validators.max(999), Validators.min(0)]],
      sideWallNo: [row.sideWallNo, [Validators.required, Validators.max(999999), Validators.min(0)]],
      lastUpdtTimestamp: [row.lastUpdtTimestamp]
    });
  }

  private updateDisplayedRowsForm(rows: PreMoldTread[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.formBuilder.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }
  getControl(item: PreMoldTread, controlName: keyof PreMoldTread): FormControl {
    const index = this.treadsList.findIndex((row) => row.treadDimensionPk === item.treadDimensionPk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }
  openImageDialog(event: Event, hexString: string, item: PreMoldInfo): void {
    event.preventDefault();
    this.selectedImage = this.displayBase64Image(hexString);
    this.imageData = this.selectedImage; // Remove duplicate prefix assignment
    this.selectedImageLabel = item.treadImageName;
    this.isImageDialogOpen = true;
  }

  displayBase64Image(base64String: string): string {
    // Remove any existing data URL prefix and return clean base64
    const cleanBase64 = base64String.replace(/^data:image\/[^;]+;base64,/, '');
    return `data:image/jpeg;base64,${cleanBase64}`;
  }

  closeImageDialog(): void {
    this.isImageDialogOpen = false;
    this.selectedImage = null;
  }

  saveDropdowns(): void {
    if (!this.saveEnabled) {
      return;
    }
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.treadsList.length; i++) {
      const original = this.treadsList[i];
      const current = formArray.at(i).value;
      // Compare original and current row (shallow compare all fields)
      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({
          treadDimensionPk: original.treadDimensionPk,
          dealerCTC: current.dealerCTC,
          dealerTreadNo: current.dealerTreadNo,
          sideWallNo: current.sideWallNo,
          sideWallWidth: current.sideWallWidth
        });
      }
    }
    if (modifiedRows.length > 0) {
      const payload: PaginatedPreMoldInfo = {
        treads: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updatePreMolds(payload).subscribe({
        next: () => {
          // Sync treadsList with the current form values to reflect the saved state
          const formArray = this.displayedRowsForm.get('rows') as FormArray;
          for (let i = 0; i < this.treadsList.length; i++) {
            const currentFormValue = formArray.at(i).value;
            this.treadsList[i] = { ...this.treadsList[i], ...currentFormValue };
          }

          this.cdr.markForCheck();
          this.isModified = false;
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.treadsList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.formGroup.value, true);
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  /**
   * Generates filters for backend call. If a filter's array length matches the master list, it is set to [].
   */
  private generateFilters(): PreMoldFilters {
    const formFilters = this.formGroup.value as PreMoldFilters;
    return {
      treads: formFilters.treads?.length === this.treads?.length ? [] : formFilters.treads,
      treadBrands: formFilters.treadBrands?.length === this.treadBrands?.length ? [] : formFilters.treadBrands,
      treadWidths: formFilters.treadWidths?.length === this.treadWidths?.length ? [] : formFilters.treadWidths,
      casingSizes: formFilters.casingSizes?.length === this.casingSizes?.length ? [] : formFilters.casingSizes,
      axles: formFilters.axles?.length === this.axles?.length ? [] : formFilters.axles,
      applications: formFilters.applications?.length === this.applications?.length ? [] : formFilters.applications
    };
  }

  checkIfModified(): boolean {
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.treadsList.length; i++) {
      const original = this.treadsList[i];
      const current = formArray.at(i).value;
      const isModified = Object.keys(current).some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...current, treadDimensionPk: original.treadDimensionPk });
      }
    }
    return modifiedRows.length > 0;
  }
}
